import { ReactNode, memo, useEffect, useRef, useState } from "react";

import { LockOpen, Warning } from "@material-ui/icons";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Input, Radio, Select } from "antd";
import { selectViewLock, useMapStore } from "stores/map";
import { supportsMaxBins } from "utils/groupBy/supportsMaxBins";

import { BinType, DataEnum, DataType, QuantileState } from "models";
import { mBinSize } from "models/binSize";
import { EntityKind } from "models/entityKind";
import { QuantileType } from "models/quantileType";

import { Tooltip } from "components/base";
import { useUserDefaults } from "components/user/hooks";

import {
  BinHeaderActions,
  BinIconButton,
  BinInputLabel,
  BinInputWrapper,
  BinLockButton,
  BinOptionsRow,
  BinSettingsContent,
  BinSettingsContentGrid,
  BinUpdateButton,
  LockIconWrapper,
  LockMessageWrapper,
  MaxBinsGrid,
  RadioGroupWrapper,
  StyledInput,
  WarningContainer,
  Wrapper
} from "./BinSettings.styles";
import DateBinSettings from "./DateBinSettings";
import MaxBinCriteriaSelect from "./MaxBinCriteriaSelect";
import { getStoredBinsFromSession } from "./util/getStoredBinsFromSession";

interface BinSettingsProps {
  bin: mBinSize;
  dataType?: DataType;
  entityKind?: EntityKind;
  canEditBin: boolean;
  onLock?: (boolean?) => void;
  onReset: () => void;
  onBinChange: (any) => void;
  onBinSettingChange: (
    binType: BinType,
    quantile: QuantileState,
    useDynamicBins: boolean
  ) => void;
  isMinCountWarningVisible?: boolean;
  groupByPropertyName?: string;
  isBinningFromAxis?: boolean;
  // Offers Link to Axis in Max Bin Criteria; the text is what it resolves to.
  linkedAxisLabel?: string;
  // Leads the icon actions.
  headerActions?: ReactNode;
  // Values picked from Others or a raised Max Bins hold every setting.
  isLocked?: boolean;
}

export type BinFields = "BinSize" | "MaxBins" | "LessThan" | "GreaterThan" | "MinSize";

function BinSettings({
  bin,
  entityKind = EntityKind.Well,
  canEditBin,
  onLock,
  onReset,
  onBinChange,
  onBinSettingChange,
  isMinCountWarningVisible,
  groupByPropertyName,
  dataType,
  isBinningFromAxis = false,
  linkedAxisLabel,
  headerActions,
  isLocked = false
}: BinSettingsProps) {
  const isIntegerDataType = dataType === DataEnum.Integer;
  const numberRegex = isIntegerDataType ? /^-?\d+$/ : /^-?\d*(\.\d*)?$/;
  const [numQuantile, setNumQuantile] = useState<number>(4); // Initialize with default, let useEffect handle restoration
  const [isEmptyBinSizeWarningVisible, setIsEmptyBinSizeWarningVisible] = useState(false);
  const viewLocked = useMapStore(selectViewLock);
  const initializedFieldRef = useRef<Maybe<string>>(null);
  const showsMaxBins = supportsMaxBins(canEditBin, dataType);

  const userDefaults = useUserDefaults();

  const handleBinTextChange = (key: BinFields) => (e) => {
    const value = e.target.value;

    if (value === "") {
      onBinChange({ [key]: null });
    } else if (numberRegex.test(value)) {
      if (isIntegerDataType) {
        onBinChange({ [key]: Math.round(Number(value)) });
      } else {
        onBinChange({ [key]: value });
      }
    }
    if (key === "BinSize" && !value && value !== 0) {
      setIsEmptyBinSizeWarningVisible(true);
    } else if (key === "BinSize" && (value || value === 0)) {
      setIsEmptyBinSizeWarningVisible(false);
    }
  };

  const [selectedQuantileType, setSelectedQuantileType] = useState<QuantileType>(
    "NumberOfQuantile" // Initialize with default, let useEffect handle restoration
  );
  const binType =
    bin.BinType === "BinSize" && bin.UseDynamicBins && !isBinningFromAxis
      ? "Dynamic"
      : bin.BinType;
  const defaultBinType =
    userDefaults?.binSettings?.type === "BinSize" &&
    userDefaults?.binSettings?.useDynamicBins &&
    !isBinningFromAxis
      ? "Dynamic"
      : userDefaults?.binSettings?.type;
  const finalBinType = canEditBin ? binType ?? defaultBinType ?? "BinSize" : "BinSize";

  function onBinKeyDown(event, binType: BinType, quantileType) {
    if (event.key === "Enter" && !isEmptyBinSizeWarningVisible) {
      onBinSettingChange(
        binType,
        {
          quantileType,
          numQuantiles: numQuantile
        },
        binType === "Dynamic"
      );
      if (canEditBin) {
        onLock?.(true);
      }
    }
  }
  const handleRefreshBinSettings = () => {
    return () => {
      onReset();
      if (bin.BinType !== "Quantile") return;

      // If the bin type is Quantile, restore the quantile settings from user defaults
      if (userDefaults?.binSettings?.quantile?.quantileType === "NumberOfQuantile") {
        setNumQuantile(userDefaults?.binSettings?.quantile?.numQuantiles ?? 4);
      }

      setSelectedQuantileType(
        userDefaults?.binSettings?.quantile?.quantileType ?? "NumberOfQuantile"
      );
    };
  };
  useEffect(() => {
    // Only trigger onBinSettingChange if the current bin type is actually Quantile
    // This prevents interfering with other bin types (BinSize, Dynamic)
    if (bin.BinType === "Quantile") {
      onBinSettingChange(
        bin.BinType,
        {
          quantileType: selectedQuantileType,
          numQuantiles: numQuantile
        },
        false // Quantile bins are never dynamic
      );
    }
    //dont want exhaustive since it will cause infinite looping
  }, [selectedQuantileType, numQuantile, bin.BinType]);

  useEffect(() => {
    if (bin.BinType === "Quantile" && !selectedQuantileType) {
      setSelectedQuantileType("NumberOfQuantile");
    }
  }, [selectedQuantileType, bin]);

  // Reset initialization tracking when field changes
  useEffect(() => {
    initializedFieldRef.current = null;
  }, [groupByPropertyName]);

  // Reset quantile state when field changes to prevent carrying over values from previous fields
  useEffect(() => {
    // Always reset quantile state when field changes, regardless of bin type
    // This prevents values from previous fields carrying over to new fields

    // Check if we have stored settings for this field
    const storedBins = getStoredBinsFromSession();
    const storedBin = storedBins[groupByPropertyName];

    if (
      storedBin?.BinType === "Quantile" &&
      storedBin.Quantile?.quantileType &&
      storedBin.Quantile?.numQuantiles
    ) {
      // Field has stored quantile settings, use them
      setSelectedQuantileType(storedBin.Quantile.quantileType);
      setNumQuantile(storedBin.Quantile.numQuantiles);
    } else {
      // Field has no stored quantile settings, reset to defaults
      if (userDefaults?.binSettings?.quantile) {
        setSelectedQuantileType(
          userDefaults.binSettings.quantile.quantileType ?? "NumberOfQuantile"
        );
        setNumQuantile(userDefaults.binSettings.quantile.numQuantiles ?? 4);
      } else {
        setSelectedQuantileType("NumberOfQuantile");
        setNumQuantile(4);
      }
    }
  }, [groupByPropertyName, userDefaults]);

  // If a user changes their group by focus field (resource play to Hz length) reset to default quantile type and number of quantile.
  // Restore quantile settings from bin prop when switching to Quantile bin type
  useEffect(() => {
    // Only restore quantile settings when bin type is Quantile AND bin has quantile data
    if (bin.BinType !== "Quantile") return;

    // Prevent re-initialization for the same field to avoid override loops
    const fieldKey = `${groupByPropertyName}_${bin.BinType}`;
    if (initializedFieldRef.current === fieldKey) return;

    // Only restore if bin already has quantile settings (from GroupByFilterActivity session storage restoration)
    // The field-level restoration is handled by the effect above
    if (bin.Quantile?.quantileType && bin.Quantile?.numQuantiles) {
      setSelectedQuantileType(bin.Quantile.quantileType);
      setNumQuantile(bin.Quantile.numQuantiles);
    }

    // Mark this field as initialized
    initializedFieldRef.current = fieldKey;
  }, [groupByPropertyName, bin.BinType, bin.Quantile]);

  // Handle warning message when bins are brought in from groupBy change
  useEffect(() => {
    if (bin.BinSize || !canEditBin || bin.BinSize === 0) {
      setIsEmptyBinSizeWarningVisible(false);
    } else if (!bin.BinSize && bin.BinSize !== 0 && canEditBin) {
      setIsEmptyBinSizeWarningVisible(true);
    }
  }, [bin.BinSize, canEditBin]);

  const getBinSizeInput = () => {
    if (dataType === "Date") {
      return (
        <BinSettingsContentGrid columnCount={3}>
          <DateBinSettings
            bin={bin}
            handleBinSizeChange={(binSize: number) => {
              onBinChange({
                ["BinSize"]: binSize
              });
              setIsEmptyBinSizeWarningVisible(false);
            }}
            handleGreaterThanChange={(greaterThan: number) =>
              onBinChange({
                ["GreaterThan"]: greaterThan
              })
            }
            handleLessThanChange={(lessThan: Maybe<number>) =>
              onBinChange({
                ["LessThan"]: lessThan
              })
            }
            canEditBin={true}
            selectedQuantileType={""}
          />
        </BinSettingsContentGrid>
      );
    } else {
      const InputGrid = showsMaxBins ? MaxBinsGrid : BinSettingsContentGrid;
      return (
        <InputGrid columnCount={canEditBin ? 4 : 3}>
          <Tooltip title="Min Count">
            <BinInputWrapper>
              <BinInputLabel>Min count</BinInputLabel>
              <StyledInput
                data-testid="minCountInput"
                type="number"
                disabled={isLocked}
                min={0}
                value={bin.MinSize}
                onChange={handleBinTextChange("MinSize")}
                onKeyDown={(evt) => onBinKeyDown(evt, bin.BinType, selectedQuantileType)}
                // The Max Bins row draws its own border.
                style={{
                  borderColor: isMinCountWarningVisible
                    ? "red"
                    : showsMaxBins
                    ? undefined
                    : "black"
                }}
              />
            </BinInputWrapper>
          </Tooltip>
          {showsMaxBins && (
            <>
              <Tooltip title="Max Bins">
                <BinInputWrapper>
                  <BinInputLabel>Max Bins</BinInputLabel>
                  <StyledInput
                    data-testid="maxBinsInput"
                    type="text"
                    disabled={isLocked}
                    value={bin.MaxBins}
                    onChange={handleBinTextChange("MaxBins")}
                    onKeyDown={(evt) =>
                      onBinKeyDown(evt, bin.BinType, selectedQuantileType)
                    }
                  />
                </BinInputWrapper>
              </Tooltip>
              <BinInputWrapper>
                <BinInputLabel>Max Bin Criteria</BinInputLabel>
                <MaxBinCriteriaSelect
                  bin={bin}
                  disabled={isLocked}
                  entityKind={entityKind}
                  linkedAxisLabel={linkedAxisLabel}
                  onChange={onBinChange}
                />
              </BinInputWrapper>
            </>
          )}
          {canEditBin && (
            <>
              <Tooltip title="Less Than">
                <BinInputWrapper>
                  <BinInputLabel>Less than</BinInputLabel>
                  <StyledInput
                    data-testid="binLessThanInput"
                    type="text"
                    disabled={!canEditBin || isLocked}
                    value={bin.LessThan ?? ""}
                    onChange={handleBinTextChange("LessThan")}
                    onKeyDown={(evt) =>
                      onBinKeyDown(evt, bin.BinType, selectedQuantileType)
                    }
                  />
                </BinInputWrapper>
              </Tooltip>
              <Tooltip title="Bin Size">
                <BinInputWrapper>
                  <BinInputLabel>Bin size</BinInputLabel>
                  <StyledInput
                    data-testid="binSizeInput"
                    type="text"
                    disabled={!canEditBin || isLocked}
                    value={bin.BinSize}
                    onChange={handleBinTextChange("BinSize")}
                    onKeyDown={(evt) =>
                      onBinKeyDown(evt, bin.BinType, selectedQuantileType)
                    }
                  />
                </BinInputWrapper>
              </Tooltip>
              <Tooltip title="Greater Than">
                <BinInputWrapper>
                  <BinInputLabel>Greater than</BinInputLabel>
                  <StyledInput
                    data-testid="binGreaterThanInput"
                    type="text"
                    disabled={!canEditBin || isLocked}
                    value={bin.GreaterThan ?? ""}
                    onChange={handleBinTextChange("GreaterThan")}
                    onKeyDown={(evt) =>
                      onBinKeyDown(evt, bin.BinType, selectedQuantileType)
                    }
                  />
                </BinInputWrapper>
              </Tooltip>
            </>
          )}
        </InputGrid>
      );
    }
  };

  return (
    <Wrapper>
      <BinSettingsContent>
        <BinOptionsRow>
          <RadioGroupWrapper>
            <Select
              data-testid="bin-type-select"
              disabled={isLocked}
              value={finalBinType}
              onChange={(val) => {
                onBinSettingChange(
                  val,
                  {
                    quantileType: selectedQuantileType,
                    numQuantiles: numQuantile
                  },
                  val === "Dynamic"
                );
              }}
              options={(canEditBin && dataType != "Date" //date can only be bin size
                ? isBinningFromAxis
                  ? ["Bin Size", "Quantile"]
                  : ["Bin Size", "Quantile", "Dynamic"]
                : ["Bin Size"]
              ).map((item) => ({
                value: item.replace(" ", ""),
                label: item
              }))}
            />
          </RadioGroupWrapper>
          <BinHeaderActions>
            {headerActions}
            {bin.UseDynamicBins && canEditBin && onLock && (
              <BinLockButton
                type="link"
                icon={bin.IsLocked ? <LockIconWrapper /> : <LockOpen />}
                onClick={() => onLock()}
                disabled={isLocked}
                data-testid="bin-lock-button"
              />
            )}
            <Tooltip title="Reset to Default">
              <BinIconButton
                type="button"
                aria-label="Reset to Default"
                onClick={handleRefreshBinSettings()}
                disabled={isLocked}
                data-testid="bin-reset-button">
                <RefreshIcon style={{ fontSize: 20 }} />
              </BinIconButton>
            </Tooltip>
          </BinHeaderActions>
        </BinOptionsRow>
        {finalBinType !== "Quantile" && getBinSizeInput()}
        {finalBinType == "Quantile" && (
          <>
            <Radio.Group
              disabled={isLocked}
              value={
                selectedQuantileType ?? userDefaults?.binSettings?.quantile?.quantileType
              }
              onChange={(evt) => {
                setSelectedQuantileType(evt.target.value);
                onBinSettingChange(
                  bin.BinType,
                  {
                    quantileType: evt.target.value,
                    numQuantiles: numQuantile
                  },
                  bin.BinType === "Dynamic"
                );
              }}>
              <Radio.Button value="NumberOfQuantile"># of Quantile</Radio.Button>
              <Tooltip title="InterQuartile Range (Q1-Q3)">
                <Radio.Button value="IQR">IQR</Radio.Button>
              </Tooltip>
            </Radio.Group>
            <Input
              placeholder="Number of Quantiles"
              type="number"
              disabled={bin.Quantile?.quantileType != "NumberOfQuantile" || isLocked}
              step={1}
              value={numQuantile ?? userDefaults?.binSettings?.quantile?.numQuantiles}
              onChange={(evt) => {
                setNumQuantile(parseInt(evt.target.value));
              }}
              min={2}
              max={12}
            />
            {viewLocked && (
              <LockMessageWrapper>
                <LockIconWrapper /> Map locked to prevent distribution changes
              </LockMessageWrapper>
            )}
          </>
        )}
        {isMinCountWarningVisible && (
          <WarningContainer data-testid="warning-message">
            <Warning style={{ top: 3 }} />{" "}
            {
              "Focus has been updated due to additional filters. Please review the min count bin."
            }
          </WarningContainer>
        )}
        {isEmptyBinSizeWarningVisible && canEditBin && (
          <WarningContainer data-testid="warning-empty-bin-size-message">
            <Warning style={{ top: 3 }} /> {"Numeric fields require a bin size."}
          </WarningContainer>
        )}
        {finalBinType != "Quantile" && (
          <BinUpdateButton
            data-testid="updateBinSettingsButton"
            onClick={() => {
              onBinSettingChange(
                binType,
                {
                  quantileType: selectedQuantileType,
                  numQuantiles: numQuantile
                },
                binType === "Dynamic"
              );
              if (canEditBin) {
                onLock?.(true);
              }
            }}
            disabled={(isEmptyBinSizeWarningVisible && canEditBin) || isLocked}
            type="primary">
            Update Bin Settings
          </BinUpdateButton>
        )}
      </BinSettingsContent>
    </Wrapper>
  );
}

export default memo(BinSettings);
